import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import MinusButton from "../../../../components/MinusButton";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import { toast } from "../../../../components/toastNotification";
import {
  SecurityAddState,
  securityAttachments,
  securityAttachmentsForDelete,
  SecurityEditState,
  securityFromOffice,
  securityOfficeId,
  securityOfficeStatus,
  securityOfficeState,
  securityOfficeBrand,
  SecuritySaving,
  setLinkStateSecurity,
  rowForEditSecurity,
  attachDisplay,
  controlFields,
  securityOnchangeCategory,
} from "../atoms/atoms";
import { useHistory } from "react-router";
import { mainSelectedTab } from "../../atoms";
import { useFileCategories } from "../hooks/useFileCategories";
import { useOfficesForSecurity } from "../hooks/useOffices";
import { ICategory, ISecurityDetails } from "../types";
import AlarmForm from "./AlarmForm";
import CameraForm from "./CameraForm";
import SafeForm from "./SafeForm";
import DoorBuzzerForm from "./DoorBuzzerForm";
import OfficeKeysForm from "./OfficeKeysForm";
import HvacForm from "./HvacForm";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import CancelButton from "../../../../components/CancelButton";
import { CarrierAgreementMgmtTabs } from "../../carrierAgreementTabs";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import PlusButton from "../../../../components/PlusButton";
import CardReaderSection from "./CardReaderSection";
import FireAlarmSystemForm from "./FireAlarmSystemForm";
import ConfirmBoxModal from "components/ConfirmBoxModal";

interface SecurityDetailsProps {
  rowForEdit: ISecurityDetails | undefined;
  isEdit: boolean;
  filesImages?: Array<ICategory>;
}
const defaultFilesImages = [
  {
    parentRow: true,
    tableName: "fileCategoryList",
    fileCategory: "",
  },
] as Array<ICategory>;

const SecurityDetails: React.FC<SecurityDetailsProps> = ({
  rowForEdit,
  isEdit,
  filesImages = defaultFilesImages,
}) => {
  const formMethods = useFormContext();

  const { control, watch, setValue } = formMethods;

  const isAddState = useRecoilValue(SecurityAddState);
  const [isEditState, setEditState] = useRecoilState(SecurityEditState);
  const [showFromOffice, setShowFromOffice] =
    useRecoilState(securityFromOffice);
  const [clickCount, setClickCount] = useState(0);
  const secOfficeId = useRecoilValue<any>(securityOfficeId);
  const secOfficeState = useRecoilValue<any>(securityOfficeState);
  const secOfficeStatus = useRecoilValue<any>(securityOfficeStatus);
  const secOfficeBrand = useRecoilValue<any>(securityOfficeBrand);
  const [rowEdit, setRowForEdit] = useRecoilState<any>(rowForEditSecurity);
  const [isSaving, setIsSaving] = useRecoilState(SecuritySaving);
  const setSelectedIndex = useSetRecoilState(mainSelectedTab);
  const isLinkedState = useRecoilValue(setLinkStateSecurity);
  const attachmentsForDelete = useRecoilValue(securityAttachmentsForDelete);
  const [confirmation, setConfirmation] = useState(false);
  const history = useHistory();
  const userPermissions = useRecoilValue(permissionsSelector);

  // Attachment
  const [attachments, setAttachments] =
    useRecoilState<(File | null)[]>(securityAttachments);
  const [attachmentDisplay, setAttachmentDisplay] = useState<any>(
    watch("fileNames") ? watch("fileNames") : []
  );
  const setAttachDisplay = useSetRecoilState<any>(attachDisplay);
  const [, setHasFirstFieldValue] = useState(true);
  const setAttachmentForDelete = useSetRecoilState<any>(
    securityAttachmentsForDelete
  );
  const [saveModal, setSaveModal] = useState(false);
  const [onchangeCategory, setOnchangeCategory] = useRecoilState(
    securityOnchangeCategory
  );

  //For removing attachments
  const removeFile = (fileName) => {
    setAttachmentDisplay(
      attachmentDisplay.filter((x) => x.securityFileName !== fileName)
    );
    setAttachments(attachments.filter((x) => x?.name !== fileName));
    setAttachmentForDelete([
      ...attachmentsForDelete,
      attachmentDisplay.filter((x) => x.securityFileName === fileName)[0],
    ]);
  };

  //For removing both file category and attachments
  const removeFilesCategory = (files) => {
    for (let x = 0; x < attachmentDisplay.length; x++) {
      for (let y = 0; y < files.length; y++) {
        if (
          attachmentDisplay[x]?.securityFileName !== files[y]?.securityFileName
        ) {
          setAttachmentDisplay([{ ...attachmentDisplay[y] }]);
        }
      }
    }
    setAttachmentForDelete(files);
  };

  const filesSection = useRef<any>();
  //#region Add/minus of forms
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name:
      filesImages.length > 0 && filesImages[0].tableName
        ? filesImages[0].tableName
        : "",
  });
  useEffect(() => {
    if (filesImages) {
      replace(filesImages);
    }
  }, []);

  const tableArray =
    filesImages.length > 0 && watch(`${filesImages[0].tableName}`);
  const controlledFields =
    tableArray &&
    fields.map((field, index) => ({
      ...field,
      ...tableArray[index],
    }));

  const setControlFields = useSetRecoilState(controlFields);

  useEffect(() => {
    setControlFields(controlledFields);
  }, []);

  useEffect(() => {
    setAttachDisplay(attachmentDisplay);
  }, [attachmentDisplay]);

  useEffect(() => {
    if (!isEditState || !isAddState) setAttachments([]);
  }, [isEditState, isAddState, setAttachments]);

  useEffect(() => {
    if (showFromOffice) {
      formMethods.setValue("officeId", secOfficeId);
      formMethods.setValue("officeState", secOfficeState);
      formMethods.setValue("officeStatus", secOfficeStatus);
      formMethods.setValue("officeBrand", secOfficeBrand);
    }
    if (isEditState) {
      formMethods.setValue("officeState", rowForEdit?.officeState);
      formMethods.setValue("officeStatus", rowForEdit?.officeStatus);
      formMethods.setValue("officeBrand", rowForEdit?.officeBrand);
    }
  }, [showFromOffice, isEditState]);

  useEffect(() => {
    if (showFromOffice) {
      filesSection?.current?.focus();
    }
  }, [showFromOffice]);

  const { data: offices } = useOfficesForSecurity();
  const sfOfficeNameOpt = offices?.data?.map((x) => {
    return {
      value: x.officeId,
      label: x.officeName,
      officeStatus: x.officeStatus,
      officeState: x.officeState,
      officeBrand: x.officeBrand,
    };
  });

  const { data: FileCategories } = useFileCategories();
  const fileCategoriesOption = FileCategories?.data?.map(
    (x: { id: number; securityFileCategory1: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.securityFileCategory1,
      };
    }
  );

  const handleCancel = () => {
    if (showFromOffice) {
      setShowFromOffice(false);
      handleLinkOffice("officesetting");
    }
    setRowForEdit(undefined);
    setEditState(false);
    setIsSaving(false);
  };

  // redirection back to office
  const handleLinkOffice = (moduleName) => {
    const url = `/carrieragreementmgmt/${moduleName}`;
    history.push(moduleName);
    let userTabs = [
      {
        to: "",
      },
    ];
    [...CarrierAgreementMgmtTabs].forEach((tabs) => {
      let temp = [...userPermissions].filter(
        (user) => user === tabs.permission
      );
      if (temp.length > 0)
        userTabs.push({
          to: tabs.to,
        });
    });

    userTabs.shift();
    const index = userTabs.findIndex((c) => c.to === url);
    setSelectedIndex(index);
  };

  const handleLink = () => {
    history.push({
      pathname: "/officesettings/security",
    });
    window.location.reload();
  };

  const toastNoFirstFile = () => {
    setHasFirstFieldValue(false);
    toast.fire(
      "Information",
      "Please fill the file category field and choose a file to proceed.",
      "info"
    );
  };
  const checkEmptyFields = (buttonState) => {
    if (buttonState === "add") {
      setClickCount(clickCount + 1);
      watch("fileCategoryList[0].id")
        ? setHasFirstFieldValue(true)
        : toastNoFirstFile();
    }
  };

  const [onChangeValue, setOnChangeValue] = useState("");
  const [indexValue, setIndexValue] = useState("");
  const confirmSelection = () => {
    setConfirmation(false);
    setOnChangeValue(onChangeValue);
  };
  const [values, setValues] = useState("");
  const fileCategoryValue = (e, index) => {
    setValue(`${filesImages[0].tableName}[${index}].id`, e);

    setConfirmation(false);
  };

  const removeFilesForCategory = (fileList, e) => {
    if (!fileList?.length) return;

    if (e === null) {
      if (fileList.length > 1) {
        removeFilesCategory(fileList);
      } else if (fileList.length === 1) {
        removeFile(fileList[0].securityFileName);
      }
    }
  };

  const updateFileCategoryInt = (removedValue) => {
    const current = watch("fileCatergoryInt");
    if (!current) return;

    setValue(
      "fileCatergoryInt",
      current
        .split(",")
        .filter((x) => Number(x) !== removedValue)
        .toString()
    );
  };

  const handleFileCategoryChange =
    ({ field, index }) =>
    (e) => {
      const previousValue = field.value;
      const nextValue = e?.value ?? null;

      const fileList = attachmentDisplay?.filter(
        (x) => x.fileCategoryId === previousValue
      );

      setValues(previousValue);
      field.onChange(nextValue);

      if (nextValue !== null && previousValue !== undefined) {
        setOnChangeValue(nextValue);
        setIndexValue(index);
        setConfirmation(true);
      } else {
        removeFilesForCategory(fileList, e);
      }
      updateFileCategoryInt(previousValue);
    };

  const selectedCategoryIds = useMemo(
    () => controlledFields?.map((c) => c.id) ?? [],
    [controlledFields]
  );

  return (
    <div className="bg-bgDB">
      <div className="mx-2 mt-2 bg-white box-shadow rounded-lg h-full">
        <div className="text-quinary font-bold font-sans py-2 px-4">
          Main Section
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 mx-4 mr-5 pb-2 lg:gap-12 gap-2">
          <div className="space-y-1 mr-6">
            {!isAddState && (
              <TextInputLabelHookForm
                name="recordNumber"
                label="Security Number"
                type="text"
                register={formMethods.register("recordNumber", {})}
                maxLength={50}
                disabled={true}
                required={true}
              />
            )}

            {isAddState ? (
              <Controller
                name="officeId"
                control={formMethods.control}
                rules={{ required: true }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                      formMethods.setValue(
                        "officeState",
                        e ? e.officeState : null
                      );
                      formMethods.setValue(
                        "officeStatus",
                        e ? e.officeStatus : null
                      );
                      formMethods.setValue(
                        "officeBrand",
                        e ? e.officeBrand : null
                      );
                    }}
                    value={field.value}
                    options={sfOfficeNameOpt}
                    label="Office Name"
                    className={
                      formMethods.formState.errors.officeId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={true}
                    isDisabled={secOfficeId}
                  />
                )}
              />
            ) : (
              <TextInputLabelHookForm
                name="officeName"
                label="Office Name"
                type="text"
                register={formMethods.register("officeName", {})}
                maxLength={255}
                required={false}
                disabled={true}
              />
            )}
          </div>
          <div className="space-y-1 mr-6">
            <TextInputLabelHookForm
              name="officeState"
              label="Office State"
              type="text"
              register={formMethods.register("officeState", {})}
              maxLength={50}
              disabled={true}
            />
            <TextInputLabelHookForm
              name="officeBrand"
              label="Office Brand"
              type="text"
              register={formMethods.register("officeBrand", {})}
              maxLength={50}
              disabled={true}
            />
            <TextInputLabelHookForm
              name="officeStatus"
              label="Office Status"
              type="text"
              register={formMethods.register("officeStatus", {})}
              maxLength={50}
              disabled={true}
            />
          </div>
        </div>
      </div>
      <AlarmForm rowForEdit={rowForEdit} />
      <CameraForm rowForEdit={rowForEdit} />
      <CardReaderSection
        recNumber={rowEdit?.recordNumber}
        data={rowEdit?.cardReaders}
        securityFormAction={isAddState ? "ADD" : "EDIT"}
      />
      <FireAlarmSystemForm rowForEdit={rowForEdit} />
      <SafeForm rowForEdit={rowForEdit} />
      <DoorBuzzerForm rowForEdit={rowForEdit} />
      <OfficeKeysForm rowForEdit={rowForEdit} />
      <HvacForm rowForEdit={rowForEdit} />

      <div className="mx-2 mt-2 mb-4 bg-white box-shadow rounded-lg h-full">
        <div className="flex flex-row  justify-between items-center">
          <div className="text-quinary font-bold font-sans py-2 px-4">
            Files and Images Section
          </div>

          {!isAddState && (
            <div className="flex justify-between gap-2 mr-2">
              <SubmitHookForm
                type="submit"
                disabled={false}
                buttonText={isSaving ? "Saving.." : "Save"}
                className={undefined}
                onClick={undefined}
              />
              <CancelButton
                onClick={() => {
                  if (isLinkedState) {
                    handleLink();
                  }
                  handleCancel();
                }}
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 mx-4 pb-2 lg:lg:gap-12 gap-2  gap-2">
          <label className="ml-8 pl-4 text-xs">File Category</label>
          <label className="text-xs">
            <b className="text-italic text-red-500 ">Note:</b> Kindly choose a
            file to upload
          </label>
        </div>
        {controlledFields?.map((field, index) => {
          const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.item(0);
            if (!file) return;

            const fileName = file.name;
            const isDuplicate = attachmentDisplay.some(
              (obj) => obj.securityFileName === fileName
            );

            if (isDuplicate) {
              showDuplicateError(e);
              return;
            }

            readFile(file, () => {
              addAttachment(file);
              e.target.value = "";
            });
          };

          const readFile = (file: File, onLoad: () => void) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = onLoad;
          };

          const addAttachment = (file: File) => {
            setAttachmentDisplay((prev) => [
              ...prev,
              {
                securityFileName: file.name,
                fileCategoryId: watch(
                  `${filesImages[0].tableName}[${index}].id`
                ),
              },
            ]);

            formMethods.setValue("fileName", file);
            setAttachments((prev = []) => [...prev, file]);
          };

          const showDuplicateError = (
            e: React.ChangeEvent<HTMLInputElement>
          ) => {
            toast.fire("Attachment failed", "File already exists", "error");
            e.target.value = "";
          };
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 mx-4 pb-2 lg:lg:gap-12 gap-2  gap-2">
              <div key={field.id} className="flex flex-row items-center gap-2">
                <div>
                  {index > 0 ? (
                    <MinusButton
                      className="mr-5"
                      type="button"
                      onClick={() => {
                        setValue(
                          "fileCatergoryInt",
                          watch("fileCatergoryInt")
                            ?.split(",")
                            .filter((x) => Number(x) !== field.id)
                            .toString()
                        );
                        remove(index);
                        //To remove attachments
                        const fileList = attachmentDisplay?.filter(
                          (x) => x.fileCategoryId === field.id
                        );
                        if (fileList?.length > 1) {
                          removeFilesCategory(fileList);
                        } else if (fileList?.length === 1) {
                          removeFile(fileList[0]?.securityFileName);
                        }
                      }}
                    />
                  ) : (
                    <PlusButton
                      type="button"
                      onClick={(e) => {
                        var array = Array.from(
                          new Set(
                            attachmentDisplay.map((x) => x.fileCategoryId)
                          )
                        )?.length;

                        if (
                          controlledFields.length < 5 &&
                          controlledFields.length === array
                        ) {
                          checkEmptyFields("add");
                          watch(`fileCategoryList[0].id`) &&
                            append({
                              tableName: filesImages[0]?.tableName,
                              parentRow: false,
                            });
                        } else {
                          toast.fire(
                            "Information",
                            "Please fill the file category field and choose a file to proceed.",
                            "info"
                          );
                        }
                      }}
                    />
                  )}
                </div>

                <Controller
                  name={`${filesImages[0].tableName}[${index}].id`}
                  control={formMethods.control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <SelectHookFormSingle
                      field={field}
                      value={field.value ?? null}
                      onChange={handleFileCategoryChange({
                        field,
                        index,
                      })}
                      options={fileCategoriesOption?.filter(
                        (option) =>
                          !selectedCategoryIds.includes(option.id) ||
                          option.id === field.value
                      )}
                      className={
                        formMethods.formState.errors.id &&
                        "border-2 border-red-500 col-span-2 "
                      }
                      required={false}
                    />
                  )}
                />
              </div>

              <div className="text-xs space-y-1">
                <div className="">
                  {onchangeCategory && onchangeCategory?.length > 0
                    ? attachmentDisplay?.length > 0 &&
                      attachmentDisplay
                        ?.filter(
                          (y) =>
                            y.fileCategoryId ===
                              rowEdit?.fileCategoryList[index]?.id ||
                            y.fileCategoryId ===
                              watch(`${filesImages[0].tableName}[${index}].id`)
                        )
                        .map((file, i) => {
                          return (
                            <div
                              key={i}
                              className="flex bg-gray-50 rounded-md justify-between px-2 py-1"
                            >
                              <div className="font-semibold text-left text-primary pr-2">
                                {file.securityFileName}
                              </div>
                              <div className="flex items-center">
                                <MinusButton
                                  onClick={() => {
                                    removeFile(file.securityFileName);
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })
                    : attachmentDisplay?.length > 0 &&
                      attachmentDisplay
                        ?.filter(
                          (y) =>
                            y.fileCategoryId ===
                            watch(`${filesImages[0].tableName}[${index}].id`)
                        )
                        .map((file, i) => {
                          return (
                            <div
                              key={i}
                              className="flex bg-gray-50 rounded-md justify-between px-2 py-1"
                            >
                              <div className="font-semibold text-left text-primary pr-2">
                                {file.securityFileName}
                              </div>
                              <div className="flex items-center">
                                <MinusButton
                                  onClick={() => {
                                    removeFile(file.securityFileName);
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                </div>
                <div className="flex justify-center">
                  <label
                    className={
                      controlledFields[index]?.id === undefined ||
                      controlledFields[index]?.id === null
                        ? "opacity-50 text-center bg-primary text-white py-1 px-3 border font-medium rounded-full transition duration-500"
                        : "text-center bg-primary text-white py-1 px-3 border font-medium rounded-full cursor-pointer transition duration-500 hover:bg-white hover:text-primary hover:border-primary"
                    }
                  >
                    <input
                      type="file"
                      className="hidden"
                      disabled={
                        controlledFields[index]?.id === undefined ||
                        controlledFields[index]?.id === null
                      }
                      onChange={handleFileChange}
                    />
                    Choose File
                  </label>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={filesSection} tabIndex={-1}></div>
      </div>
      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Security" : "Edit Security"}
          qConfirmation={`Are you sure you want to ${
            isAddState ? "Add " : "Update "
          } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            setIsSaving(true);
          }}
          cancelButtonRef={undefined}
        />
      )}
      {confirmation && (
        <ConfirmBoxModal
          ModalTitle={"Change File Category"}
          qConfirmation={`Are you sure you want to change the file category?`}
          isOpen={confirmation}
          onClose={() => {
            setConfirmation(false);
          }}
          onClickFunction={() => {
            if (isAddState) {
              const fileList = attachmentDisplay?.filter(
                (x) => x.fileCategoryId === values
              );
              if (fileList?.length > 1 && confirmation) {
                removeFilesCategory(fileList);
              } else {
                removeFile(fileList[0]?.securityFileName);
              }
              fileCategoryValue(onChangeValue, indexValue);
              confirmSelection();
            } else {
              const onchange = attachmentDisplay.filter(
                (files) => files.fileCategoryId === values
              );
              const val = onchange.map((x) => ({
                ...x,
                fileCategoryId: onChangeValue,
              }));
              setOnchangeCategory(val);
              setAttachmentDisplay(
                attachmentDisplay.map((x) =>
                  x.fileCategoryId === values
                    ? { ...x, fileCategoryId: onChangeValue }
                    : x
                )
              );
              fileCategoryValue(onChangeValue, indexValue);
              confirmSelection();
            }
          }}
          cancelButtonRef={undefined}
        />
      )}
    </div>
  );
};
export default SecurityDetails;
